import { useT } from '../i18n/index.ts';
import { navigate } from '../lib/router.ts';
import { Icon, type IconName } from './icons.tsx';
import { Brand } from './parts.tsx';

export type StatusKind = 'notFound' | 'server' | 'offline';

/** Whom to write to when a problem keeps coming back (same address as the sign-in page). */
const SUPPORT_CONTACT = 'kharcha@kcloudai.com';

const LOOK: Record<StatusKind, { code: string; icon: IconName; tint: string; strip: string; tips: IconName[] }> = {
  notFound: { code: '404', icon: 'search', tint: 'bg-brand-tint text-brand', strip: 'bg-brand', tips: ['search', 'house'] },
  server: { code: '500', icon: 'alert', tint: 'bg-danger-tint text-danger', strip: 'bg-danger', tips: ['check', 'sync', 'clock'] },
  offline: { code: '', icon: 'cloudOff', tint: 'bg-warning-tint text-warning-text', strip: 'bg-warning', tips: ['check', 'history', 'sync'] },
};

/**
 * Full-page "Page not found", "Something went wrong" and "You're offline" screens, in English
 * and Urdu, drawn as a torn paper slip like the sign-in card: what happened, what is still safe,
 * and what to do next. `homePath` is "/" for spenders and "/admin" for owners.
 */
export function StatusPage({
  kind,
  homePath = '/',
  onRetry,
}: {
  kind: StatusKind;
  homePath?: string;
  /** Shown as "Try again" on server/offline pages. */
  onRetry?: () => void;
}) {
  const { t } = useT();
  const look = LOOK[kind];
  const goHome = () => navigate(homePath, { replace: true });
  // Phones: stacked, full width, full 48 px tap height (flex-1 in a column would squash them);
  // from sm: side by side, sharing the row.
  const primary =
    'flex h-12 w-full shrink-0 items-center justify-center gap-2 rounded-xl bg-ink px-4 text-base font-bold text-white hover:bg-ink-soft sm:w-auto sm:flex-1';
  const secondary =
    'flex h-12 w-full shrink-0 items-center justify-center gap-2 rounded-xl border-[1.5px] border-line-strong bg-paper px-4 text-base font-semibold hover:bg-ground sm:w-auto sm:flex-1';

  return (
    <div className="safe-top safe-bottom flex min-h-app flex-col bg-ground">
      <header className="flex h-[72px] shrink-0 items-center px-4 sm:px-6 lg:px-10">
        <Brand />
      </header>
      <main className="flex flex-1 items-center justify-center px-4 pt-4 pb-12">
        <div className="receipt-edge w-full max-w-[440px] rounded-t-2xl bg-paper shadow-[0_12px_32px_-16px_rgb(19_33_31/0.35)]">
          <div className={`h-1.5 rounded-t-2xl ${look.strip}`} />

          <div className="flex flex-col gap-5 px-6 pt-7 pb-8 sm:px-8">
            <div className="flex items-center gap-4">
              <span className={`flex size-14 shrink-0 items-center justify-center rounded-2xl ${look.tint}`}>
                <Icon name={look.icon} size={28} stroke={2} />
              </span>
              <div className="flex min-w-0 flex-col gap-1">
                {look.code && (
                  <span className="num w-fit rounded-md bg-ground px-2 py-0.5 text-xs font-bold tracking-[0.12em] text-muted uppercase">
                    {t('errors.code', { code: look.code })}
                  </span>
                )}
                <h1 className="m-0 text-[22px] leading-tight font-bold rtl:text-[25px]">{t(`errors.${kind}Title`)}</h1>
              </div>
            </div>

            <p className="m-0 text-[15px] leading-relaxed text-muted rtl:text-[17px]">{t(`errors.${kind}Body`)}</p>

            <div className="rule-dashed" />

            <ul className="m-0 flex list-none flex-col gap-3 p-0">
              {look.tips.map((icon, i) => (
                <li key={i} className="flex items-start gap-3 text-[15px] rtl:text-[17px]">
                  <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-ground text-ink">
                    <Icon name={icon} size={14} stroke={2.5} />
                  </span>
                  <span className="min-w-0">{t(`errors.${kind}Tip${i + 1}`)}</span>
                </li>
              ))}
            </ul>

            <div className="rule-dashed" />

            <div className="flex flex-col gap-3 sm:flex-row">
              {onRetry ? (
                <button type="button" onClick={onRetry} className={primary}>
                  <Icon name="sync" size={20} />
                  {t('errors.tryAgain')}
                </button>
              ) : (
                <button type="button" onClick={goHome} className={primary}>
                  <Icon name="house" size={20} />
                  {t('errors.goHome')}
                </button>
              )}
              {kind === 'notFound' ? (
                <button type="button" onClick={() => window.history.back()} className={secondary}>
                  <Icon name="arrowStart" size={20} flip />
                  {t('errors.goBack')}
                </button>
              ) : (
                onRetry && (
                  <button type="button" onClick={goHome} className={secondary}>
                    <Icon name="house" size={20} />
                    {t('errors.goHome')}
                  </button>
                )
              )}
            </div>

            {kind === 'server' && (
              <p className="m-0 text-center text-[13px] text-muted rtl:text-[15px]">
                {t('errors.stillStuck')}
                <br />
                <a href={`mailto:${SUPPORT_CONTACT}`} className="num font-semibold text-brand underline-offset-2 hover:underline">
                  {SUPPORT_CONTACT}
                </a>
              </p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
